/**
 * PartnersLogoTape - Scrolling tape of partner/provider logos
 *
 * Displays trusted provider logos in an infinite scrolling tape animation.
 * Used on the hero landing page before platform activation.
 *
 * @example
 * ```tsx
 * <PartnersLogoTape className="mt-8" />
 * ```
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { PARTNER_LOGOS } from '@/shared/components/full-site/constants/heroConstants';

interface PartnersLogoTapeProps {
  /** Additional CSS classes */
  className?: string;
  /** Hide the section label */
  hideLabel?: boolean;
}

export const PartnersLogoTape: React.FC<PartnersLogoTapeProps> = ({
  className = '',
  hideLabel = false,
}) => {
  const { t } = useTranslation();

  return (
    <div className={`w-full max-w-4xl px-6 ${className}`}>
      {!hideLabel && (
        <div className="section-label">
          {t('hero.heroAIChat.trustedProviders', 'Trusted Providers')}
        </div>
      )}
      <div className="tape-mask">
        <div className="tape-track">
          {/* Duplicate logos for seamless infinite scroll */}
          {[...PARTNER_LOGOS, ...PARTNER_LOGOS].map((logo, i) => (
            <img
              key={`${logo.name}-${i}`}
              src={logo.logo}
              alt={logo.name}
              width={logo.width}
              height={logo.height}
              className={`partner-logo partner-logo-${logo.short.toLowerCase()}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default PartnersLogoTape;
